import React, { useEffect, useMemo, useState } from 'react';
import { CloseLight } from '../icons/icons';
import './SuccessLauncher.scss';
import FAQs from './FAQs';
import { NavigationPage } from './successlauncher-types';
import ResourceCenter from './ResourceCenter';
import SubPage from './SubPage';
import { getDescriptionForPage, getPagesItems } from './PageContent';
import { BackArrow } from './Icons';
import FeedbackForm from './FeedbackForm';
import { getFaqPage } from './helpers';
import VideoPage from './VideoPage';
import ErrorBoundary from '../components/ErrorBoundary';
import { useLocation, useNavigate, Location } from 'react-router-dom';

export interface SuccessLauncherProps {
  onClose: () => void;
  firstName: string;
  location: Location;
}

export default function SuccessLauncher(props: SuccessLauncherProps) {
  const { onClose } = props;
  const navigate = useNavigate();
  const location = useLocation();
  const [currentNavigation, setCurrentNavigation] = useState<NavigationPage[]>([]);
  const selectedPage = currentNavigation.length > 0 ? currentNavigation[currentNavigation.length - 1] : null;
  const setSelectedPage = (page: NavigationPage) => setCurrentNavigation([...currentNavigation, ...[page]]);
  const PagesItems = useMemo(() => getPagesItems(location, navigate), [location, navigate]);

  useEffect(() => {
    const onKeyDown = (e) => {
      // close on Esc
      if (e.keyCode === 27) {
        onClose();
      }
    };
    document.body.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.removeEventListener('keydown', onKeyDown);
    };
  }, [onClose]);

  let title = '';
  let description = '';
  let body = null;
  switch (selectedPage?.page) {
    case 'Videos':
    case 'Product Tours':
    case 'Provide Feedback':
      title = selectedPage.page;
      description = getDescriptionForPage(selectedPage.page);
      body = <SubPage pageItems={PagesItems.filter((x) => x.page === selectedPage.page)} onClose={props.onClose} setSelectedPage={setSelectedPage} />;
      break;

    case 'Feedback Form':
      switch (selectedPage.feedbackType) {
        case 'FormsTemplates':
          title = 'Forms, Templates, & Public Forms';
          break;
        case 'Account':
          title = 'Account Management';
          break;
        case 'Workflows':
          title = 'Workflows, Integrations, & Automations';
          break;
        case 'Reports':
          title = 'Reports';
          break;
        case 'MobleApps':
          title = 'Mobile Apps';
          break;
        case 'NotSure':
          title = 'Not Sure';
          break;
      }
      description = 'When you post an idea to our feedback forum, others will be able to subscribe to it and make comments.';
      body = <FeedbackForm onClose={props.onClose} feedbackType={selectedPage.feedbackType} />;
      break;

    case 'Video Page':
      switch (selectedPage.videoCategory) {
        case 'Getting_Started':
          title = 'Getting Started';
          description = 'Follow these simple steps to start using GoFormz';
          break;
        case 'Templates':
          title = selectedPage.videoCategory;
          description = 'Learn to build and customize your templates';
          break;
        case 'Forms':
          title = selectedPage.videoCategory;
          description = 'Learn how to fill out forms on mobile devices and online';
          break;
        case 'ProTips':
          title = selectedPage.videoCategory;
          description = 'Explore best practices and helpful tips';
          break;
        case 'Webinar':
          title = selectedPage.videoCategory;
          description = 'Learn about a variety of topics from our team';
          break;
      }
      body = <VideoPage category={selectedPage.videoCategory} />;
      break;

    default:
      title = 'Resource Center';
      body = <ResourceCenter selectPage={setSelectedPage} onClose={props.onClose} />;
      break;
  }

  return (
    <div className="success-launcher">
      <div className="success-launcher-header">
        <h2>Hello {props.firstName}!</h2>
        <button className="close-button plain-button" tabIndex={-1} onClick={props.onClose}>
          <CloseLight />
        </button>
      </div>
      <div className="success-launcher-body">
        <h2>{title}</h2>
        {description ? <div className="description">{description}</div> : null}
        {selectedPage ? (
          <button className="back-button" onClick={() => setCurrentNavigation(currentNavigation.slice(0, currentNavigation.length - 1))}>
            <BackArrow />
          </button>
        ) : null}
        <ErrorBoundary>{body}</ErrorBoundary>
        {/* keep FAQs rendering so that they keep their state */}
        <div style={{ display: selectedPage === null ? 'block' : 'none' }}>
          <ErrorBoundary>
            <FAQs page={getFaqPage(props.location.pathname)} />
          </ErrorBoundary>
        </div>
      </div>
    </div>
  );
}
